import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconButton, Skeleton, Stack, Typography } from '@mui/material';
import {
  Background,
  Wrapper,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/styles';
import DeviceManager from '@/components/util/deviceManager';
import { ChevronLeftIcon } from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { ButtonsContainer } from './components/ButtonsContainer';
import { TextContainer } from './components/TextContainer';
import { useNavigationStore } from '../../../../../store';
import { PopupProps } from './types';

const IMAGE_HEIGHT = 192;

export const NavigationIntroPopup: React.FC<PopupProps> = ({
  open,
  handleBack,
  item,
  contentLoaded,
  onConfirm,
  rtl,
  tour,
  setPopupHeight,
  setSharePopupOpen,
  sharePopupOpen,
  sharingEnabled,
  mobilePosition,
  pathFinderLine,
  hiddenFromLink,
}) => {
  const [isVisible, setIsVisible] = useState(open);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = DeviceManager.isMobile();
  const translation = useNavigationStore((state) => state.navigationTranslation);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useLayoutEffect(() => {
    const textHeight = textContainerRef.current?.offsetHeight;
    const titleHeight = titleContainerRef.current?.offsetHeight;
    const height = textHeight + titleHeight + IMAGE_HEIGHT;
    if (height) setPopupHeight(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, mobilePosition]);

  const renderImage = useCallback(() => {
    return item?.tag?.webPathThumbnail && item?.tag?.navigationThumbnail ? (
      <img
        alt="Thumbnail"
        src={`${process.env.NEXT_PUBLIC_CDN}/tours/${item.tourId}/tags/icons/${item.tag.navigationThumbnail}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      <img
        alt={tour.name}
        src={`${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${tour.mainImage}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }, [item, tour]);

  if (!hiddenFromLink && !item) return null;

  return (
    <>
      <Background
        open={open}
        isVisible={isVisible}
        style={{
          background: 'none',
          backdropFilter: 'blur(0px)',
          position: isMobile ? 'absolute' : 'relative',
          marginTop: isMobile ? 'unset' : '',
          height: isMobile ? '100%' : 'auto',
          display: open ? '' : 'none',
          width: isMobile ? '100%' : 'fit-content',
        }}
      >
        <Wrapper
          open={open}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: sharePopupOpen ? 'none' : '',
            position: isMobile ? 'relative' : 'static',
            padding: '0px',
            overflow: 'hidden',
            width: isMobile ? 'auto' : '340px',
            height: isMobile ? '100%' : 'auto',
            border: isMobile ? 'none' : '1px solid #FFFFFF',
            transform: 'none',
            animation: 'none',
            marginLeft: isMobile ? '0' : '',
            background: isMobile ? 'none' : '#fff',
          }}
        >
          <Stack
            ref={titleContainerRef}
            sx={{
              p: '14px',
              background: '#F7F7F7',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            direction="row"
          >
            <IconButton
              sx={{
                position: 'absolute',
                left: '14px',
                borderRadius: '1rem',
                padding: '4px',
                background: '#EAECF0',
                width: '32px',
                height: '32px',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
              onClick={handleBack}
            >
              <ChevronLeftIcon style={{}} />
            </IconButton>
            <Typography
              fontSize="16px"
              fontWeight={rtl ? 400 : 500}
              sx={{ direction: rtl ? 'rtl' : 'ltr', color: '#000000' }}
            >
              {translation.navDestination || 'Destination'}
            </Typography>
          </Stack>
          {isMobile && (
            <Stack ref={textContainerRef} p={0} m={0}>
              <TextContainer
                rtl={rtl}
                contentLoaded={contentLoaded}
                item={item}
                isMobile={isMobile}
              />
            </Stack>
          )}

          <Stack style={{ width: '100%', height: `${IMAGE_HEIGHT}px`, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: rtl ? 'row-reverse' : 'row',
                width: '100%',
                height: '100%',
              }}
            >
              {contentLoaded ? (
                renderImage()
              ) : (
                <Skeleton variant="rounded" width="100%" height="100%" />
              )}
            </div>
          </Stack>

          <Stack sx={{ p: 0 }}>
            {!isMobile && (
              <TextContainer
                rtl={rtl}
                contentLoaded={contentLoaded}
                item={item}
                isMobile={isMobile}
              />
            )}
          </Stack>
        </Wrapper>
      </Background>
      {open && !sharePopupOpen && (
        <ButtonsContainer
          isMobile={isMobile}
          buttonsDisabled={!contentLoaded}
          onConfirm={onConfirm}
          setSharePopupOpen={setSharePopupOpen}
          sharingEnabled={sharingEnabled}
          rtl={rtl}
        />
      )}
    </>
  );
};
